import { ButtonBase, ClickAwayListener, Fade, Paper, Popper } from "@mui/material";
import { COLOR_BLACK, COLOR_GREY, COLOR_GREY_BORDER, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { AccountGroupOutline, AccountOutline, MenuDown } from "mdi-material-ui";
import DropdownItem, { DROPDOWN_FONT_SIZE } from "molecules/DropdownItem";
import GamePlayerSelector from "pages/games/GamePlayerSelector";
import React, { useState } from "react";

export const ALL_TEAMS = "all";

function Dropdown({ icon: Icon, buttonLabel, disabled = false, testId, children }) {
  const [open, openSet] = useState(false);
  const [anchorEl, anchorElSet] = useState(null);

  const handleToggle = (event) => {
    anchorElSet(event.currentTarget);
    openSet((prev) => !prev);
  };

  const handleClose = () => openSet(false);

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      <ButtonBase
        onClick={handleToggle}
        disabled={disabled}
        data-testid={testId}
        style={{
          border: `1px solid ${COLOR_GREY_BORDER}`,
          borderRadius: 12,
          padding: "6px 16px",
          backgroundColor: COLOR_WHITE_LIGHTER,
          color: COLOR_BLACK,
          textTransform: "none",
          fontSize: DROPDOWN_FONT_SIZE,
          display: "flex",
          alignItems: "center",
          gap: 8,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <Icon style={{ fontSize: "1.2em" }} />
        <span
          style={{
            whiteSpace: "nowrap",
            maxWidth: 250,
            overflow: "hidden",
            fontFamily: FONT_FAMILY_GRAPHIK,
            fontWeight: 500,
            letterSpacing: "0.01em",
            textOverflow: "ellipsis",
          }}
        >
          {buttonLabel}
        </span>
        <MenuDown
          sx={{
            fontSize: "1.2em",
            color: COLOR_GREY,
            transition: "transform 0.2s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </ButtonBase>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        style={{ zIndex: 2, paddingTop: 4 }}
        modifiers={[{ name: "flip", enabled: false }]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <div>
              <ClickAwayListener onClickAway={handleClose}>
                <Paper
                  elevation={0}
                  style={{
                    borderRadius: 8,
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
                  }}
                >
                  {children(handleClose)}
                </Paper>
              </ClickAwayListener>
            </div>
          </Fade>
        )}
      </Popper>
    </div>
  );
}

export function PlayerListDropdown({
  game,
  teamId = null,
  selectedPlayerId,
  onSelect,
  onTeamSelect,
  buttonLabel = "Select player",
  disabled = false,
  isLoading = false,
}) {
  const players = game?.gamePlayers ?? [];
  const teamPlayers = teamId ? players.filter((player) => player.team?.id === teamId) : players;
  const buttonDisabled = disabled || (!isLoading && teamPlayers.length === 0);

  return (
    <Dropdown icon={AccountOutline} buttonLabel={buttonLabel} disabled={buttonDisabled} testId="player-dropdown">
      {(close) => (
        <GamePlayerSelector
          game={game}
          teamId={teamId}
          gamePlayerId={selectedPlayerId}
          onTeamSelect={
            onTeamSelect &&
            ((selectedTeamId) => {
              onTeamSelect(selectedTeamId);
              close();
            })
          }
          gamePlayerIdSet={(playerId) => {
            const selected = players.find((player) => player.id === playerId);
            if (selected) {
              onSelect?.(selected);
              close();
            }
          }}
        />
      )}
    </Dropdown>
  );
}

export function TeamListDropdown({ game, selectedTeamId, onSelect, disabled = false }) {
  const options = [
    { id: ALL_TEAMS, label: "Home & Away" },
    ...[game?.home, game?.away].filter(Boolean).map((team) => ({ id: team.id, label: team.name })),
  ];
  const selected = options.find((option) => option.id === selectedTeamId) || options[0];

  return (
    <Dropdown icon={AccountGroupOutline} buttonLabel={selected.label} disabled={disabled} testId="team-dropdown">
      {(close) => (
        <div style={{ display: "flex", flexFlow: "column nowrap", alignItems: "stretch", minWidth: 200 }}>
          {options.map((option) => (
            <DropdownItem
              key={option.id}
              selected={option.id === selected.id}
              onClick={() => {
                onSelect?.(option.id);
                close();
              }}
            >
              {option.label}
            </DropdownItem>
          ))}
        </div>
      )}
    </Dropdown>
  );
}
